import { ContainerWidth, SectionMargins } from "api/shop";

const VALID_CONTAINER_WIDTHS: ContainerWidth[] = ["default", "narrow", "medium", "wide", "full"];

const normalizeContainerWidth = (raw: unknown): ContainerWidth | undefined => {
  if (raw === null || raw === undefined || raw === "") return undefined;
  if (typeof raw !== "string") return undefined;
  if (raw === "default") return undefined;
  return (VALID_CONTAINER_WIDTHS as string[]).includes(raw) ? (raw as ContainerWidth) : undefined;
};

/**
 * Normalizes margins coming out of the RHF/Yup form.
 *
 * Returns `undefined` (so the request payload omits margins entirely) when:
 *  - the input is null/undefined, or
 *  - none of the four mobile/desktop values are meaningful (all missing or 0/NaN)
 *    AND no meaningful containerWidth override is set.
 *
 * Otherwise returns a clean SectionMargins object with `xl` preserved when it's a real number.
 * `containerWidth` is preserved only when set to a non-default, non-empty value.
 */
export const normalizeSectionMargins = (
  raw: Partial<SectionMargins> | undefined | null,
): SectionMargins | undefined => {
  if (!raw) return undefined;

  const toNumber = (v: unknown): number | undefined => {
    if (v === null || v === undefined || v === "") return undefined;
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : undefined;
  };

  const topMobile = toNumber(raw.marginTop?.mobile);
  const topDesktop = toNumber(raw.marginTop?.desktop);
  const topXl = toNumber(raw.marginTop?.xl);
  const bottomMobile = toNumber(raw.marginBottom?.mobile);
  const bottomDesktop = toNumber(raw.marginBottom?.desktop);
  const bottomXl = toNumber(raw.marginBottom?.xl);

  const containerWidth = normalizeContainerWidth(raw.containerWidth);

  const allMarginsMissingOrZero =
    !topMobile && !topDesktop && !bottomMobile && !bottomDesktop;

  if (allMarginsMissingOrZero && !containerWidth) return undefined;

  const result: Partial<SectionMargins> = {};

  if (!allMarginsMissingOrZero) {
    result.marginTop = {
      mobile: topMobile ?? 0,
      desktop: topDesktop ?? 0,
      ...(topXl !== undefined ? { xl: topXl } : {}),
    };
    result.marginBottom = {
      mobile: bottomMobile ?? 0,
      desktop: bottomDesktop ?? 0,
      ...(bottomXl !== undefined ? { xl: bottomXl } : {}),
    };
  }

  if (containerWidth) {
    result.containerWidth = containerWidth;
  }

  return result as SectionMargins;
};
